import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  type AddContactPayload,
  type ContactInput,
  type ContactResolveResponse,
  type SuggestedMatch,
  api,
  getErrorMessage,
} from "../api/client.ts";
import { keys } from "../api/queryKeys.ts";

// Mounted only while open, so each opening starts blank.
interface AddContactDialogProps {
  onClose: () => void;
  /** When set, the contact is added to this list; otherwise a person is created standalone. */
  listId?: string;
  /** Optional company context recorded on the list entry. */
  companyId?: string | null;
  /** The company's list entry, for a company the database does not hold. */
  companyEntryId?: string | null;
  onDone?: () => void;
}

const EMPTY = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  linkedin: "",
  reason: "",
  note: "",
};

function confidencePct(m: SuggestedMatch): string {
  return m.confidence != null ? `${Math.round(m.confidence * 100)}%` : "—";
}

export default function AddContactDialog({
  onClose,
  listId,
  companyId,
  companyEntryId,
  onDone,
}: AddContactDialogProps) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ ...EMPTY });
  const [resolved, setResolved] = useState<ContactResolveResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const contactInput = (): ContactInput => ({
    first_name: form.first_name.trim() || null,
    last_name: form.last_name.trim() || null,
    email: form.email.trim() || null,
    phone: form.phone.trim() || null,
    linkedin: form.linkedin.trim() || null,
  });

  const hasName = form.first_name.trim() || form.last_name.trim();
  const hasIdentifier = form.email.trim() || form.phone.trim() || form.linkedin.trim();
  const canCheck = Boolean(hasName && hasIdentifier);

  const afterWrite = () => {
    if (listId) {
      queryClient.invalidateQueries({ queryKey: keys.prospectListContactEntry.byList(listId) });
      queryClient.invalidateQueries({ queryKey: keys.prospectList.detail(listId) });
    }
    onDone?.();
    onClose();
  };

  const resolveMut = useMutation({
    mutationFn: () => api.resolveContact(contactInput()),
    onSuccess: (data) => {
      setResolved(data);
      setError(null);
    },
    onError: (e) => setError(getErrorMessage(e)),
    meta: { suppressErrorToast: true },
  });

  const addToList = (payload: AddContactPayload) =>
    api.addContactToList(listId!, {
      ...payload,
      master_company_id: companyId ?? null,
      company_entry_id: companyEntryId ?? null,
      reason: form.reason.trim() || null,
      note: form.note.trim() || null,
    });

  const mergeMut = useMutation({
    mutationFn: (masterContactId: string) => {
      // Send the typed identifiers too — the backend attaches any the matched
      // person doesn't have yet instead of dropping them.
      if (listId) return addToList({ ...contactInput(), master_contact_id: masterContactId });
      // Standalone: nothing to persist when merging into an existing person.
      return Promise.resolve(null);
    },
    onSuccess: afterWrite,
    onError: (e) => setError(getErrorMessage(e)),
    meta: { suppressErrorToast: true },
  });

  const createMut = useMutation({
    mutationFn: async () => {
      if (listId) {
        await addToList({ ...contactInput() });
      } else {
        await api.createMasterContact({ ...contactInput() });
      }
    },
    onSuccess: afterWrite,
    onError: (e) => setError(getErrorMessage(e)),
    meta: { suppressErrorToast: true },
  });

  const busy = resolveMut.isPending || mergeMut.isPending || createMut.isPending;

  const matches = resolved?.matches ?? [];
  const title = useMemo(
    () => (listId ? "Add contact to collection" : "New contact"),
    [listId],
  );

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={2}>
            <TextField
              label="First name"
              value={form.first_name}
              onChange={(e) => setForm({ ...form, first_name: e.target.value })}
              size="small"
              fullWidth
            />
            <TextField
              label="Last name"
              value={form.last_name}
              onChange={(e) => setForm({ ...form, last_name: e.target.value })}
              size="small"
              fullWidth
            />
          </Stack>
          <TextField
            label="Email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            size="small"
            fullWidth
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="Phone"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              size="small"
              fullWidth
            />
            <TextField
              label="LinkedIn"
              value={form.linkedin}
              onChange={(e) => setForm({ ...form, linkedin: e.target.value })}
              size="small"
              fullWidth
            />
          </Stack>
          {listId && (
            <>
              <TextField
                label="Reason (why add?)"
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                size="small"
                fullWidth
              />
              <TextField
                label="Note"
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                size="small"
                fullWidth
                multiline
                rows={2}
              />
            </>
          )}

          <Button
            variant="outlined"
            onClick={() => resolveMut.mutate()}
            loading={resolveMut.isPending}
            disabled={!canCheck || busy}
          >
            Check for existing contacts
          </Button>

          {resolved && matches.length > 0 && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Possible existing matches — merge to avoid a duplicate:
              </Typography>
              <List dense>
                {matches.map((m) => (
                  <ListItem
                    key={m.master_contact_id}
                    secondaryAction={
                      <Button
                        size="small"
                        variant="contained"
                        loading={mergeMut.isPending && mergeMut.variables === m.master_contact_id}
                        disabled={busy}
                        onClick={() => mergeMut.mutate(m.master_contact_id)}
                      >
                        Merge
                      </Button>
                    }
                  >
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                          <span>{m.full_name ?? "(no name)"}</span>
                          <Chip
                            size="small"
                            label={`${m.tier} · ${confidencePct(m)}`}
                            color={m.acceptable ? "success" : "default"}
                          />
                          {!m.verified && (
                            <Chip size="small" label="unverified" variant="outlined" />
                          )}
                        </Stack>
                      }
                      secondary={
                        [m.reachability.email, m.reachability.phone, m.reachability.linkedin_url]
                          .filter(Boolean)
                          .join(" · ") || null
                      }
                    />
                  </ListItem>
                ))}
              </List>
              <Divider sx={{ my: 1 }} />
            </Box>
          )}

          {resolved && matches.length === 0 && (
            <Alert severity="info">No existing contact matched — safe to create a new one.</Alert>
          )}

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3 }}>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => createMut.mutate()}
          loading={createMut.isPending}
          disabled={!canCheck || busy}
        >
          {listId ? "Create & add" : "Create contact"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
